/**
 * Rider "Complete delivery" sheet for orders placed with Location Service Engine delivery proof: the customer's
 * 4-digit code, a photo (required or optional as the tenant set it) and the rider's GPS position at the door.
 * Submits to ``POST /api/rider/lse/orders/{id}/deliver``, which verifies everything server-side and then closes the
 * order through the normal rider path (cash collected for COD). The code itself is never sent to the rider app.
 */
import { useEffect, useState } from "react";
import { Camera, CheckCircle2, KeyRound, Loader2, MapPin, X } from "lucide-react";
import { toast } from "sonner";
import { api, inr } from "../../lib/api";
import ImageUploader from "../ImageUploader";

export function LseRiderPlan({ order }) {
    const plan = order?.delivery_plan;
    if (!plan && !order?.route) return null;
    return (
        <div data-testid="lse-rider-plan" className="rounded-2xl border border-border bg-card px-4 py-3 text-sm">
            {plan && <p className="font-semibold">{plan.label}{plan.date_label ? ` · ${plan.date_label}` : ""}{plan.slot?.label ? ` · ${plan.slot.label}` : ""}</p>}
            {order.route?.village_name && <p className="text-xs text-muted-foreground mt-0.5">{order.route.village_name} · {order.route.route_name} route</p>}
            {order.delivery_proof?.otp_required && <p className="text-xs text-muted-foreground mt-0.5 inline-flex items-center gap-1"><KeyRound size={12} /> Needs the customer's delivery code</p>}
        </div>
    );
}

export default function LseDeliverSheet({ order, onClose, onDone }) {
    const dp = order.delivery_proof || {};
    const cod = (order.payment_method || "").toUpperCase() === "COD" && !order.cdc_enabled;
    const [otp, setOtp] = useState("");
    const [photo, setPhoto] = useState("");
    const [pos, setPos] = useState(null);
    const [gpsState, setGpsState] = useState(dp.gps ? "locating" : "off");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!dp.gps || !navigator.geolocation) { if (dp.gps) setGpsState("unavailable"); return; }
        navigator.geolocation.getCurrentPosition(
            (p) => { setPos({ lat: p.coords.latitude, lng: p.coords.longitude }); setGpsState("ok"); },
            () => setGpsState("unavailable"),
            { enableHighAccuracy: true, maximumAge: 10000, timeout: 10000 },
        );
    }, [dp.gps]);

    const ready = (!dp.otp_required || /^\d{4}$/.test(otp)) && (dp.photo !== "REQUIRED" || !!photo)
        && !(dp.gps === "ENFORCED" && gpsState !== "ok");

    const submit = async () => {
        setBusy(true);
        try {
            await api.post(`/rider/lse/orders/${order.id}/deliver`, {
                otp: dp.otp_required ? otp : undefined, photo_url: photo || undefined,
                lat: pos?.lat, lng: pos?.lng,
            });
            toast.success(cod ? "Cash collected · delivered ✨" : "Delivered ✨");
            onDone?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't complete the delivery"); } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" role="dialog" aria-modal="true" aria-label="Complete delivery">
            <div className="bg-card w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-5 max-h-[92vh] overflow-y-auto" style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }} data-testid="lse-deliver-sheet">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold">Complete delivery</h2>
                    <button type="button" onClick={onClose} aria-label="Close" className="p-2 -mr-2"><X size={20} /></button>
                </div>
                {cod && <p className="mt-1 text-sm">Collect <strong>{inr(order.totals?.grand_total)}</strong> in cash.</p>}

                {dp.otp_required && (
                    <label className="block mt-4">
                        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1"><KeyRound size={12} /> Customer's delivery code</span>
                        <input inputMode="numeric" pattern="\d{4}" maxLength={4} autoFocus value={otp}
                               onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))}
                               data-testid="lse-deliver-otp"
                               className="mt-1 w-full rounded-2xl bg-muted px-4 py-3 text-2xl font-bold tracking-[0.5em] text-center tabular-nums" />
                    </label>
                )}

                {dp.photo && (
                    <div className="mt-4">
                        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1"><Camera size={12} /> Photo of the delivered order {dp.photo === "REQUIRED" ? "" : "(optional)"}</p>
                        <div className="mt-1 max-w-[180px]">
                            <ImageUploader value={photo} onUploaded={(url) => setPhoto(url)} testId="lse-deliver-photo" aspect="aspect-[4/3]" />
                        </div>
                    </div>
                )}

                {dp.gps && (
                    <p className={`mt-4 text-xs inline-flex items-center gap-1.5 ${gpsState === "ok" ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"}`} data-testid="lse-deliver-gps">
                        <MapPin size={13} />
                        {gpsState === "locating" && "Getting your location…"}
                        {gpsState === "ok" && "Location captured"}
                        {gpsState === "unavailable" && (dp.gps === "ENFORCED" ? "Turn on location to complete this delivery" : "Location unavailable — you can still complete it")}
                    </p>
                )}

                <button type="button" onClick={submit} disabled={!ready || busy} data-testid="lse-deliver-submit"
                        className="mt-5 w-full min-h-[52px] rounded-2xl bg-foreground text-background font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                    {busy ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />} {cod ? "Cash collected · delivered" : "Delivered"}
                </button>
            </div>
        </div>
    );
}
